import React, { useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { ArrowLeft, Scale, Crown } from 'lucide-react';
import { BrandLockup } from './BrandLogo';
import { Button } from './ui/button';
import { Card } from './ui/card';
import api from '../services/api';
import { Seo, PUBLIC_PAGES } from '../seo/Seo';
import SupportedCitiesPage from './SupportedCitiesPage';
import AboutUs from './AboutUs';
import PrivacyPolicy from './PrivacyPolicy';
import TermsOfService from './TermsOfService';
import Contact from './Contact';
import HelpCenter from './HelpCenter';
import SafetyTips from './SafetyTips';
import CommunityGuidelines from './CommunityGuidelines';
import ReportIssues from './ReportIssues';
import CookiePolicy from './CookiePolicy';
import DataProcessingAgreement from './DataProcessingAgreement';
import AgeVerificationPolicy from './AgeVerificationPolicy';
import RefundPolicy from './RefundPolicy';

// Footnote keys used by LandingPage/Settings -> canonical public URL
export const LEGAL_PATHS = {
  about: '/about', privacy: '/privacy', terms: '/terms', contact: '/contact', help: '/help', safety: '/safety',
  guidelines: '/community-guidelines', report: '/report', 'cookie-policy': '/cookies', 'data-processing': '/data-processing',
  'age-verification': '/age-verification', 'refund-policy': '/refund-policy', legal: '/legal', premium: '/premium',
};
const ALIASES = { '/terms-of-service': '/terms', '/privacy-policy': '/privacy', '/refund': '/refund-policy', '/cookie-policy': '/cookies',
  '/guidelines': '/community-guidelines', '/pricing': '/premium' };
const COMPONENTS = { '/about': AboutUs, '/privacy': PrivacyPolicy, '/terms': TermsOfService, '/contact': Contact, '/help': HelpCenter,
  '/safety': SafetyTips, '/community-guidelines': CommunityGuidelines, '/report': ReportIssues, '/cookies': CookiePolicy,
  '/data-processing': DataProcessingAgreement, '/age-verification': AgeVerificationPolicy, '/refund-policy': RefundPolicy };
const TITLES = { '/about': 'About Us', '/privacy': 'Privacy Policy', '/terms': 'Terms of Service', '/contact': 'Contact & Support', '/help': 'Help Center',
  '/safety': 'Safety Tips', '/community-guidelines': 'Community Guidelines', '/report': 'Report an Issue', '/cookies': 'Cookie Policy',
  '/data-processing': 'Data Processing Agreement', '/age-verification': 'Age Verification Policy', '/refund-policy': 'Payments & Refunds',
  '/legal': 'Legal Center', '/premium': 'FlameLink Premium — Pricing' };
const LEGAL_CENTER_LINKS = [
  ['/terms', 'Terms of Service', 'The agreement that governs use of FlameLink.'],
  ['/privacy', 'Privacy Policy', 'What we collect, why, and your choices.'],
  ['/refund-policy', 'Payments & Refunds', 'Premium subscriptions, cancellation and refunds.'],
  ['/premium', 'Premium Pricing', 'Plans, prices and billing intervals.'],
  ['/cookies', 'Cookie Policy', 'Cookies and similar technologies.'],
  ['/community-guidelines', 'Community Guidelines', 'Expected behaviour on FlameLink.'],
  ['/safety', 'Safety Tips', 'Staying safe while dating.'],
  ['/age-verification', 'Age Verification Policy', 'FlameLink is for adults 18+.'],
  ['/data-processing', 'Data Processing Agreement', 'How personal data is processed.'],
  ['/contact', 'Contact & Support', 'How to reach us.'],
];

const PageShell = ({ title, onBack, children, testId }) => (
  <div className="min-h-screen bg-gradient-to-br from-rose-50 via-pink-50 to-orange-50" data-testid={testId}>
    <header className="bg-white/80 backdrop-blur-sm border-b border-rose-100 sticky top-0 z-10">
      <div className="container mx-auto px-4 py-4 flex items-center justify-between">
        <Button variant="ghost" onClick={onBack} className="text-gray-600 hover:text-gray-900" data-testid={`${testId}-back-btn`}><ArrowLeft className="w-4 h-4 mr-2" />Back</Button>
        <BrandLockup size="sm" testId="public-brand-lockup" />
      </div>
    </header>
    <main className="container mx-auto px-4 py-10 max-w-3xl space-y-6">
      <h1 className="text-3xl md:text-4xl font-bold text-gray-900">{title}</h1>
      {children}
    </main>
  </div>
);

const LegalCenter = ({ onBack }) => {
  const navigate = useNavigate();
  return (
    <PageShell title="Legal Center" onBack={onBack} testId="legal-center-page">
      <p className="text-gray-600 flex items-center gap-2"><Scale className="w-5 h-5 text-rose-500" />All FlameLink policies and documents, each at its own address.</p>
      <div className="grid gap-3 sm:grid-cols-2">
        {LEGAL_CENTER_LINKS.map(([path, label, blurb]) => (
          <a key={path} href={path} onClick={(e) => { e.preventDefault(); navigate(path); }} className="block rounded-xl border border-rose-100 bg-white p-4 hover:border-rose-300 transition-colors" data-testid={`legal-link-${path.slice(1)}`}>
            <p className="font-semibold text-gray-900">{label}</p>
            <p className="text-sm text-gray-500">{blurb}</p>
            <p className="text-xs text-rose-500 mt-1">flamelink.site{path}</p>
          </a>
        ))}
      </div>
    </PageShell>
  );
};

const interval = (o) => (o.interval_count > 1 ? `${o.interval_count} ${o.interval}s` : o.interval);

// Read-only fetch of the server catalog (same offers the app uses). Never mutates Stripe.
const useOffers = () => {
  const [data, setData] = useState(null);
  useEffect(() => { api.get('/payments/offers').then((r) => setData(r.data.data)).catch(() => setData({ offers: [], mode: 'test' })); }, []);
  return { data, notOnSale: !data || data.mode !== 'live' };
};

export const NOT_ON_SALE_TEXT = 'FlameLink Premium is not yet available for purchase. Prices below are published for transparency; no payments are being accepted.';
export const BILLING_TERMS_TEXT = 'Recurring subscription billed by Stripe. Cancel any time; access continues until the end of the paid period.';

// Landing-page Premium section: public prices + billing disclosure, links to /premium.
export const LandingPremiumSection = ({ onSeePricing }) => {
  const { data, notOnSale } = useOffers();
  return (
    <section id="premium" className="py-20 bg-gradient-to-br from-amber-50 via-rose-50 to-orange-50" data-testid="landing-premium-section">
      <div className="container mx-auto px-6">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 rounded-full bg-white/80 px-4 py-1.5 text-sm font-semibold text-amber-700 shadow-sm mb-4"><Crown className="w-4 h-4" /> Optional upgrade</div>
          <h2 className="text-4xl lg:text-5xl font-bold mb-4"><span className="bg-gradient-to-r from-amber-600 to-rose-600 bg-clip-text text-transparent">FlameLink Premium</span></h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">Discovering, matching and messaging are free. Premium adds unlimited likes and shows you everyone who liked you.</p>
        </div>
        {notOnSale && (
          <p className="mx-auto max-w-2xl rounded-md bg-amber-100 border border-amber-300 px-4 py-3 text-sm text-amber-900 text-center mb-8" data-testid="landing-pricing-not-available">{NOT_ON_SALE_TEXT}</p>
        )}
        <div className="grid gap-6 md:grid-cols-3 max-w-4xl mx-auto" data-testid="landing-pricing-plans">
          {(data?.offers || []).map((o) => (
            <Card key={o.lookup_key} className="p-6 bg-white/90 hover:shadow-xl transition-shadow duration-300 space-y-2" data-testid={`landing-plan-${o.lookup_key}`}>
              <div className="flex items-center gap-2"><Crown className="w-5 h-5 text-amber-500" /><p className="font-semibold text-gray-900">{o.name}</p>
                {o.badge && <span className="ml-auto rounded-full bg-rose-500 px-2 py-0.5 text-[10px] font-semibold uppercase text-white">{o.badge}</span>}</div>
              <p className="text-3xl font-bold text-gray-900">${o.amount.toFixed(2)} <span className="text-sm font-normal text-gray-500">{o.currency.toUpperCase()} / {interval(o)}</span></p>
              <p className="text-sm text-gray-500">${o.per_month.toFixed(2)} per month{o.savings_pct > 0 ? ` · save ${o.savings_pct}% vs monthly` : ''}</p>
            </Card>
          ))}
          {data && data.offers.length === 0 && <p className="text-sm text-gray-500 md:col-span-3 text-center">Pricing is temporarily unavailable.</p>}
        </div>
        <p className="mt-8 text-center text-sm text-gray-600 max-w-2xl mx-auto" data-testid="landing-pricing-terms">{BILLING_TERMS_TEXT}</p>
        <div className="mt-6 text-center">
          <Button onClick={onSeePricing} className="bg-gradient-to-r from-amber-500 to-rose-500 hover:from-amber-600 hover:to-rose-600 text-white px-8 py-3 rounded-full font-semibold shadow-lg" data-testid="landing-see-pricing-btn">See Full Pricing</Button>
        </div>
      </div>
    </section>
  );
};

// Public pricing: same server catalog the app uses; no login, no checkout from here.
const PublicPricing = ({ onBack, onGetStarted }) => {
  const navigate = useNavigate();
  const { data, notOnSale } = useOffers();
  return (
    <PageShell title="FlameLink Premium — Pricing" onBack={onBack} testId="public-pricing-page">
      <p className="text-gray-700">Creating a profile, discovering people, matching and messaging are free. FlameLink Premium is an optional recurring subscription that adds unlimited likes and lets you see everyone who liked you.</p>
      {notOnSale && (
        <p className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-800" data-testid="pricing-not-available">{NOT_ON_SALE_TEXT}</p>
      )}
      <div className="grid gap-3 sm:grid-cols-3" data-testid="public-pricing-plans">
        {(data?.offers || []).map((o) => (
          <Card key={o.lookup_key} className="p-4 space-y-1" data-testid={`public-plan-${o.lookup_key}`}>
            <div className="flex items-center gap-2"><Crown className="w-4 h-4 text-amber-500" /><p className="font-semibold text-gray-900">{o.name}</p>
              {o.badge && <span className="ml-auto rounded-full bg-rose-500 px-2 py-0.5 text-[10px] font-semibold uppercase text-white">{o.badge}</span>}</div>
            <p className="text-2xl font-bold text-gray-900">${o.amount.toFixed(2)} <span className="text-sm font-normal text-gray-500">{o.currency.toUpperCase()} / {interval(o)}</span></p>
            <p className="text-xs text-gray-500">${o.per_month.toFixed(2)} per month{o.savings_pct > 0 ? ` · save ${o.savings_pct}% vs monthly` : ''}</p>
          </Card>
        ))}
        {data && data.offers.length === 0 && <p className="text-sm text-gray-500">Pricing is temporarily unavailable.</p>}
      </div>
      <Card className="p-4 text-sm text-gray-700 space-y-2" data-testid="pricing-terms">
        <p>{BILLING_TERMS_TEXT}</p>
        <p>You can cancel at any time from Settings → FlameLink Premium. Cancelling stops future renewals; your Premium features stay active until the end of the period you have already paid for, then your account returns to Free.</p>
        <p>Full details: <a href="/refund-policy" onClick={(e) => { e.preventDefault(); navigate('/refund-policy'); }} className="text-rose-600 underline" data-testid="pricing-refund-link">Payments & Refunds</a> · <a href="/terms" onClick={(e) => { e.preventDefault(); navigate('/terms'); }} className="text-rose-600 underline" data-testid="pricing-terms-link">Terms of Service</a></p>
      </Card>
      <Button className="bg-rose-500 hover:bg-rose-600 text-white" onClick={onGetStarted} data-testid="pricing-get-started-btn">Create your free account</Button>
    </PageShell>
  );
};

// Returns the element for a public legal/info URL, or null when the path is not one of them.
export const renderPublicPage = (pathname, { onBack, onGetStarted }) => {
  const path = pathname.replace(/\/+$/, '') || '/';
  if (ALIASES[path]) return <Navigate to={ALIASES[path]} replace />;
  if (path === '/legal') return <ScrollTop key={path} title={TITLES[path]} path={path}><LegalCenter onBack={onBack} /></ScrollTop>;
  if (path === '/premium') return <ScrollTop key={path} title={TITLES[path]} path={path}><PublicPricing onBack={onBack} onGetStarted={onGetStarted} /></ScrollTop>;
  if (path === '/cities') return <ScrollTop key={path} title="Supported Cities" path={path}><SupportedCitiesPage onBack={onBack} onGetStarted={onGetStarted} /></ScrollTop>;
  const Comp = COMPONENTS[path];
  if (Comp) return <ScrollTop key={path} title={TITLES[path]} path={path}><Comp onBack={onBack} /></ScrollTop>;
  if (KNOWN_APP_PATHS.some((k) => path === k || path.startsWith(k + '/'))) return null;
  return <NotFoundPage onBack={onBack} />;
};

// Paths handled elsewhere in the app (never treated as not-found here)
const KNOWN_APP_PATHS = ['/', '/admin', '/payment', '/login', '/register', '/premium', '/legal'];

export const NotFoundPage = ({ onBack }) => (
  <div className="min-h-screen bg-gradient-to-br from-rose-50 to-pink-100 flex items-center justify-center p-6" data-testid="not-found-page">
    <Seo path="/404" noindex title="Page not found | FlameLink" description="This page does not exist." />
    <Card className="max-w-md w-full p-8 text-center space-y-4">
      <BrandLockup size={48} />
      <h1 className="text-2xl font-bold text-gray-900">Page not found</h1>
      <p className="text-gray-600">The address you opened doesn't exist on FlameLink. It may have moved or been typed incorrectly.</p>
      <div className="flex flex-wrap justify-center gap-2">
        <Button className="bg-rose-500 hover:bg-rose-600 text-white" onClick={onBack} data-testid="not-found-home-btn">Back to home</Button>
        <a href="/help" className="inline-flex items-center px-4 py-2 rounded-md border text-sm text-gray-700 hover:bg-white" data-testid="not-found-help-link">Help Center</a>
      </div>
    </Card>
  </div>
);

const ScrollTop = ({ title, path, children }) => {
  useEffect(() => { window.scrollTo(0, 0); }, [title]);
  return <>{PUBLIC_PAGES[path] ? <Seo path={path} /> : <Seo path={path} noindex title={`${title} | FlameLink`} />}{children}</>;
};
